import React, { useEffect, useState } from 'react';
import { motion } from 'motion/react';

interface Particle {
  id: number;
  x: number;
  y: number;
  scale: number;
  color: string;
  shape: 'circle' | 'square' | 'triangle';
  angle: number;
  spinSpeed: number;
  duration: number;
  keyframesX: number[];
  keyframesY: number[];
}

const COLORS = ['#fbbf24', '#f43f5e', '#10b981', '#38bdf8', '#a855f7', '#f97316'];
const SHAPES: ('circle' | 'square' | 'triangle')[] = ['circle', 'square', 'triangle'];

interface ConfettiProps {
  active?: boolean;
  count?: number;
}

export default function Confetti({ active = true, count = 80 }: ConfettiProps) {
  const [particles, setParticles] = useState<Particle[]>([]);

  useEffect(() => {
    if (!active) {
      setParticles([]);
      return;
    }

    const items: Particle[] = Array.from({ length: count }).map((_, i) => {
      const isLeft = Math.random() < 0.5;
      const startX = isLeft ? Math.random() * 15 : 85 + Math.random() * 15;
      const peakX = startX + (isLeft ? 1 : -1) * (Math.random() * 30 + 20);
      const driftX = peakX + (Math.random() * 20 - 10);
      
      const peakY = Math.random() * 45 + 10; // 10vh to 55vh from top
      
      return {
        id: i,
        x: startX,
        y: 105, // start at 105vh (below screen)
        scale: Math.random() * 0.6 + 0.4,
        color: COLORS[Math.floor(Math.random() * COLORS.length)],
        shape: SHAPES[Math.floor(Math.random() * SHAPES.length)],
        angle: Math.random() * 360,
        spinSpeed: Math.random() * 1080 - 540,
        duration: Math.random() * 1.5 + 2.2, // 2.2s to 3.7s total duration
        keyframesX: [startX, peakX, driftX],
        keyframesY: [105, peakY, 115]
      };
    });

    setParticles(items);
  }, [active, count]);

  if (!active || particles.length === 0) return null;

  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden z-[9999]">
      {particles.map(p => (
        <motion.div
          key={p.id}
          className="absolute"
          style={{
            left: `${p.x}%`,
            top: `${p.y}vh`,
            width: `${p.scale * 12}px`,
            height: `${p.scale * 12}px`,
            backgroundColor: p.shape !== 'triangle' ? p.color : undefined,
            borderRadius: p.shape === 'circle' ? '50%' : '2px',
            borderLeft: p.shape === 'triangle' ? `${p.scale * 6}px solid transparent` : undefined,
            borderRight: p.shape === 'triangle' ? `${p.scale * 6}px solid transparent` : undefined,
            borderBottom: p.shape === 'triangle' ? `${p.scale * 12}px solid ${p.color}` : undefined,
          }}
          animate={{
            left: p.keyframesX.map(x => `${x}%`),
            top: p.keyframesY.map(y => `${y}vh`),
            rotate: p.angle + p.spinSpeed,
          }}
          transition={{
            duration: p.duration,
            times: [0, 0.35, 1], // quick launch up, slow descent
            ease: ["easeOut", "easeIn"],
          }}
        />
      ))}
    </div>
  );
}
